import * as React from 'react';
import {useState, useEffect, useRef} from 'react';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import DirectionsIcon from '@mui/icons-material/Directions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import {useTranslation} from "react-i18next";
import ErrorResponseDialog from "./dialog/ErrorResponseDialog";
import {LoadScript} from "@react-google-maps/api";

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;

interface GeocodeResult {
    lat: number;
    lng: number;
    formatted_address: string;
    zoom: number;
}

interface GoogleMapsSearchProps {
    onLocationFound: (lat: number, lng: number, address: string, zoom: number) => void;
}

export default function GoogleMapsSearch({onLocationFound}: GoogleMapsSearchProps) {
    const {t} = useTranslation();
    const [searchValue, setSearchValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<TemplateStringsArray | string | undefined>();
    const [searchResults, setSearchResults] = useState<GeocodeResult[]>([]);
    const [showResults, setShowResults] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShowResults(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
        if (showResults) {
            setShowResults(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();

        if (!searchValue.trim()) return;

        setIsLoading(true);
        setShowResults(false);
        setSearchResults([]);

        if (!window.google || !window.google.maps) {
            setError("Google Maps JS API is not loaded");
            setIsLoading(false);
            return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: searchValue }, (results, status) => {
            if (status === "OK" && results && results.length > 0) {
                const limitedResults: GeocodeResult[] = results.slice(0, 5).map(r => ({
                    formatted_address: r.formatted_address,
                    lat: r.geometry.location.lat(),
                    lng: r.geometry.location.lng(),
                    zoom: getZoom(r.types)
                }));
                setSearchResults(limitedResults);
                setShowResults(true);
            } else {
                setError(t("search_address_no_results"));
            }
            setIsLoading(false);
        });
    };

    const handleLocationSelect = (result: GeocodeResult) => {
        const formattedAddress = result.formatted_address;
        onLocationFound(result.lat, result.lng, formattedAddress, result.zoom);
        setShowResults(false);
        setSearchResults([]);
        setSearchValue(formattedAddress);
    };

    const getZoom = (types: string[]) => {
        if (types.includes("country")) {
            return 6;
        }
        if (types.includes("administrative_area_level_1")) {
            return 8;
        }
        if (types.includes("locality")) {
            return 10;
        }
        return 14;
    }

    return (
        <LoadScript googleMapsApiKey={apiKey}>
            {error &&
                <ErrorResponseDialog errorCode={error} onClose={() => {
                    setError(undefined);
                }}/>
            }
            <Box ref={containerRef} sx={{ width: "100%", position: "relative" }}>
                <Collapse in={showResults && searchResults.length > 0}>
                    <Paper 
                        sx={{ 
                            maxHeight: 300, 
                            overflow: 'auto',
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                            borderBottom: 'none',
                            zIndex: 1000
                        }}
                        elevation={4}
                    >
                        <List dense sx={{ py: 0 }}>
                            {searchResults.map((result, index) => (
                                <ListItem key={index} disablePadding>
                                    <ListItemButton 
                                        onClick={() => handleLocationSelect(result)}
                                        sx={{
                                            borderRadius: 0,
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={result.formatted_address}
                                            primaryTypographyProps={{
                                                variant: "body2",
                                                sx: { wordBreak: 'break-word' }
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Collapse>

                <Paper
                    component="form"
                    onSubmit={handleSearch}
                    sx={{
                        p: "2px 4px", 
                        display: "flex", 
                        width: "100%",
                        ...(showResults && searchResults.length > 0 && {
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            borderTop: 'none'
                        })
                    }}
                    elevation={showResults && searchResults.length > 0 ? 4 : 1}
                >
                    <InputBase
                        sx={{ml: 1, flex: 1}}
                        placeholder={t("search_address_placeholder")}
                        value={searchValue}
                        onChange={handleInputChange}
                        inputProps={{'aria-label': 'rechercher une adresse'}}
                        disabled={isLoading}
                    />
                    <Divider sx={{height: 28, m: 0.5}} orientation="vertical"/>
                    <IconButton
                        color="primary"
                        sx={{p: '10px'}}
                        aria-label="directions"
                        onClick={handleSearch}
                        disabled={isLoading || !searchValue.trim()}
                    >
                        <DirectionsIcon/>
                    </IconButton>
                </Paper>
            </Box>
        </LoadScript>
    );
}
